import { buildWhatsAppHref } from "@/lib/whatsapp";
import type { PatientTerm } from "@/hooks/usePatientTerms";

export type TermOwnerPhone = {
  phone_number: string;
  is_primary: boolean | null;
  deleted_at: string | null;
};

/** Builds the public URL for a digitally-signed term PDF. */
export function getTermPublicUrl(termId: string, accessCode: string): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin.replace(/\/$/, "") : "";
  return `${origin}/termo/${termId}/${encodeURIComponent(accessCode)}.pdf`;
}

/**
 * Returns the public URL only when both id and access code are available,
 * otherwise returns null.
 */
export function getTermSignedPublicUrl(
  term: Pick<PatientTerm, "id"> & {
    digitally_signed_access_code?: string | null;
  },
): string | null {
  if (!term.id || !term.digitally_signed_access_code) return null;
  return getTermPublicUrl(term.id, term.digitally_signed_access_code);
}

/** Extracts active phone numbers from the owner attached to the pet, sorted by priority. */
export function getTermOwnerPhones(
  term: PatientTerm & {
    pet?: {
      owner?: {
        owners_phones?: TermOwnerPhone[] | null;
      } | null;
    } | null;
  },
): TermOwnerPhone[] {
  const phones = term.pet?.owner?.owners_phones ?? [];
  return phones
    .filter((p) => p.deleted_at == null && p.phone_number)
    .sort((a, b) => {
      const score = (p: TermOwnerPhone) => (p.is_primary ? 0 : 1);
      return score(a) - score(b);
    });
}

/** Builds the default WhatsApp message for sharing a signed term. */
export function buildTermShareMessage(
  petName: string | null | undefined,
  signedPdfUrl: string | null | undefined,
): string {
  const name = petName?.trim() || "paciente";
  const lines = [
    `Olá, aqui está o termo assinado do(a) ${name}.`,
    signedPdfUrl ? "Utilize o link abaixo para abrir o termo." : null,
    signedPdfUrl ?? null,
  ].filter(Boolean);
  return lines.join("\n");
}

/** Opens WhatsApp for sharing. Throws if the phone is invalid. */
export function shareTermViaWhatsApp(phoneNumber: string, message: string): void {
  const href = buildWhatsAppHref(phoneNumber, { text: message });
  if (!href) throw new Error("invalid_phone");
  window.open(href, "_blank", "noopener,noreferrer");
}
